import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { useDevice } from '../lib/device.js';
import { api } from '../lib/api.js';
import { useT } from '../lib/i18n.js';
import { applyBackground, usePrefs, type Language } from '../lib/prefs.js';
import { StatusBar } from './StatusBar.js';
import { SettingsModal } from './SettingsModal.js';
import { ProfileCard } from './ProfileCard.js';

export type SceneKey = 'today' | 'reflect' | 'align' | 'vault' | 'converse' | 'pro';

// Only the owner and trusted people get the full app. Everyone else (invited /
// public visitors) is limited to conversing — their own chats + their profile.
export function isPrivileged(role?: string | null): boolean {
  return role === 'owner' || role === 'trustee';
}
export function allowedScenes(user?: { role?: string | null; organizationId?: string | null } | null): SceneKey[] {
  if (isPrivileged(user?.role)) return ['today', 'reflect', 'align', 'vault', 'converse', 'pro'];
  // A colleague who belongs to a company also gets the Professional section.
  return user?.organizationId ? ['converse', 'pro'] : ['converse'];
}

interface Props {
  active: SceneKey;
  onChange: (s: SceneKey) => void;
  children: ReactNode;
}

const TABS: { key: SceneKey; label: string; icon: ReactNode }[] = [
  { key: 'today', label: 'Today', icon: <IconSun /> },
  { key: 'reflect', label: 'Reflect', icon: <IconMoon /> },
  { key: 'align', label: 'Align', icon: <IconTarget /> },
  { key: 'vault', label: 'Vault', icon: <IconVault /> },
  { key: 'converse', label: 'Converse', icon: <IconChat /> },
  { key: 'pro', label: 'Pro', icon: <IconBriefcase /> },
];

const LANGS: Language[] = ['fr', 'en', 'es'];

export function Shell({ active, onChange, children }: Props) {
  const { user } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isMobile } = useDevice();
  const tr = useT();
  const [prefs, setPrefs] = usePrefs();

  // Background gradient/accent vars (previously a side effect of StatusBar, which
  // the desktop sidebar no longer renders).
  useEffect(() => { applyBackground(prefs.background); }, [prefs.background]);

  const privileged = isPrivileged(user?.role);

  // Owner-only: badge the Align tab with the count of low-confidence answers.
  const isOwner = user?.role === 'owner';
  const { data: alignStats } = useQuery<{ stats: { needsAlignment: number } }>({
    queryKey: ['analysis', 'stats'],
    queryFn: () => api('/api/analysis/stats'),
    enabled: isOwner,
    refetchInterval: 60_000,
  });
  const needsAlign = isOwner ? (alignStats?.stats?.needsAlignment ?? 0) : 0;

  const visibleTabs = TABS.filter((t) => allowedScenes(user).includes(t.key));
  const initial = (user?.name ?? user?.email ?? '?').charAt(0).toUpperCase();
  const cycleLang = () => setPrefs({ language: LANGS[(LANGS.indexOf(prefs.language) + 1) % LANGS.length]! });

  // ── Desktop · owner/trustee: full app shell with a persistent sidebar ───────
  if (!isMobile && privileged) {
    return (
      <div className="min-h-dvh flex">
        <aside className="sticky top-0 h-dvh w-56 shrink-0 flex flex-col border-r border-ink/10 bg-white/[0.02] backdrop-blur px-3 py-5">
          <div className="flex items-center gap-2 px-2 pb-5">
            <span className="w-2 h-2 rounded-full bg-particle shadow-[0_0_10px] shadow-particle" />
            <span className="text-[14px] text-ink/90 font-medium tracking-tight">martindaigneault</span>
          </div>

          <nav className="flex flex-col gap-0.5">
            {visibleTabs.map((t) => {
              const on = active === t.key && !settingsOpen;
              return (
                <button key={t.key} onClick={() => { setSettingsOpen(false); onChange(t.key); }}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] transition-colors ${
                    on ? 'bg-amber/[0.12] text-ink border border-amber/25' : 'text-ink/55 hover:text-ink hover:bg-white/[0.03] border border-transparent'
                  }`}>
                  <span className={on ? 'text-amber' : 'text-ink/45'}>{t.icon}</span>
                  {tr('nav.' + t.key)}
                  {t.key === 'align' && needsAlign > 0 && (
                    <span className="ml-auto min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-rose-400 text-void text-[10px] leading-none">
                      {needsAlign > 99 ? '99+' : needsAlign}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="mt-auto flex flex-col gap-0.5 border-t border-ink/10 pt-3">
            <button onClick={cycleLang} title="Language"
              className="flex items-center gap-3 px-3 py-2 rounded-lg text-[12px] text-ink/45 hover:text-ink/80 hover:bg-white/[0.03]">
              <IconGlobe /> {prefs.language.toUpperCase()}
            </button>
            <button onClick={() => setSettingsOpen((v) => !v)} title="Settings"
              className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                settingsOpen ? 'bg-amber/[0.12] text-ink border border-amber/25' : 'text-ink/55 hover:text-ink hover:bg-white/[0.03] border border-transparent'
              }`}>
              <IconGear /> Settings
            </button>
            <button onClick={() => setProfileOpen(true)} title="Profile"
              className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-white/[0.03]">
              <span className="w-7 h-7 rounded-full grid place-items-center font-serif italic text-[14px] text-ink bg-gradient-to-br from-amber/60 to-amber/20">{initial}</span>
              <span className="flex flex-col text-left leading-tight">
                <span className="text-[13px] text-ink/85 truncate max-w-[120px]">{user?.name ?? user?.email}</span>
                <span className="text-[10px] text-particle/80">{user?.role}</span>
              </span>
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 pt-6 pb-16">
          {settingsOpen
            ? <SettingsModal variant="page" open onClose={() => setSettingsOpen(false)} />
            : children}
        </main>

        <ProfileCard open={profileOpen} onClose={() => setProfileOpen(false)} onOpenSettings={() => { setProfileOpen(false); setSettingsOpen(true); }} />
      </div>
    );
  }

  // ── Desktop · visitor: minimal, conversation-focused chrome (no work nav) ───
  if (!isMobile && !privileged) {
    return (
      <div className="min-h-dvh flex flex-col relative">
        <header className="fixed top-0 inset-x-0 z-50 flex items-center justify-between px-7 py-5 pointer-events-none">
          <div className="text-[11px] text-ink/60 flex items-center gap-2 pointer-events-auto">
            <span className="w-1.5 h-1.5 rounded-full bg-particle shadow-[0_0_10px] shadow-particle" />
            martindaigneault.ai
          </div>
          {visibleTabs.length > 1 && (
            <nav className="flex gap-0.5 p-1 rounded-full border border-ink/10 bg-white/[0.025] backdrop-blur pointer-events-auto">
              {visibleTabs.map((t) => (
                <button key={t.key} onClick={() => onChange(t.key)}
                  className={`px-3.5 py-1.5 rounded-full text-[12px] transition-colors ${active === t.key ? 'bg-ink text-void' : 'text-ink/45 hover:text-ink/75'}`}>
                  {tr('nav.' + t.key)}
                </button>
              ))}
            </nav>
          )}
          <div className="w-4" />
        </header>
        <main className="flex-1 pt-20 pb-20">{children}</main>
        <StatusBar onOpenSettings={() => setSettingsOpen(true)} mobile={false} />
        <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    );
  }

  // ── Mobile (any role): top bar + bottom tab bar ─────────────────────────────
  return (
    <div className="min-h-dvh flex flex-col relative">
      <header className="fixed top-0 inset-x-0 z-50 pt-safe bg-void/75 backdrop-blur border-b border-ink/[0.07]">
        <div className="flex items-center justify-between px-4 h-12">
          <div className="text-[11.5px] text-ink/65 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-particle shadow-[0_0_10px] shadow-particle" />
            martindaigneault<span className="text-ink/35">.ai</span>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-14 pb-24">{children}</main>

      <nav className="fixed bottom-0 inset-x-0 z-50 bg-void/90 backdrop-blur-xl border-t border-ink/10 pb-safe">
        <div className="flex">
          {visibleTabs.map((t) => {
            const on = active === t.key;
            return (
              <button key={t.key} onClick={() => onChange(t.key)}
                className="relative flex-1 flex flex-col items-center gap-1 pt-2 pb-1.5 active:scale-95 transition-transform">
                {t.key === 'align' && needsAlign > 0 && (
                  <span className="absolute top-1 right-[22%] min-w-[15px] h-[15px] px-1 grid place-items-center rounded-full bg-rose-400 text-void font-mono text-[8px] leading-none">
                    {needsAlign > 99 ? '99+' : needsAlign}
                  </span>
                )}
                <span className={on ? 'text-amber' : 'text-ink/45'}>{t.icon}</span>
                <span className={`text-[10px] ${on ? 'text-ink' : 'text-ink/40'}`}>
                  {tr('nav.' + t.key)}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <StatusBar onOpenSettings={() => setSettingsOpen(true)} mobile />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

/* ── Minimal line icons ── */
function IconGlobe() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
  );
}
function IconGear() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 0 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 0 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.01a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 0 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.01a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 0 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1.03z" />
    </svg>
  );
}
function IconSun() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
    </svg>
  );
}
function IconMoon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
    </svg>
  );
}
function IconTarget() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.2" />
    </svg>
  );
}
function IconVault() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 8.8v-1M12 16.2v-1" strokeLinecap="round" />
    </svg>
  );
}
function IconChat() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.2 7.7L3 21l1.8-5.8A8.5 8.5 0 1 1 21 11.5z" />
    </svg>
  );
}
function IconBriefcase() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
    </svg>
  );
}
